import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase-auth'
import { useAuth } from '../../contexts/AuthContextLazy'
import { Party } from '../../types/platform'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  LinkIcon,
  UserIcon,
  TrashIcon,
  ArchiveBoxIcon,
  PencilIcon,
  ClockIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline'
import ThreeDotsMenu from '../../components/portal/ThreeDotsMenu'
import ConfirmDialog from '../../components/portal/ConfirmDialog'
import AddPartyModal from '../../components/portal/AddPartyModal'
// import { showSuccess, showError } from '../../utils/toast'
import toast from 'react-hot-toast'
const showSuccess = (msg: string) => toast.success(msg)
const showError = (msg: string) => toast.error(msg)
import '../../styles/portal-ui-polish.css'

interface PartyWithAdvisor extends Party {
  advisor?: {
    id: string
    full_name: string
    email: string
  }
  client_mymeetings_mapping?: {
    id: string
    mymeetings_party_name: string
  }[]
  days_since_contact?: number | null
  last_contact_label?: string
  last_interaction_type?: string
  engagement_score?: number
  project_count?: number
}

export const Clients: React.FC = () => {
  const { hasRole } = useAuth()
  const [parties, setParties] = useState<PartyWithAdvisor[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [filterType, setFilterType] = useState<'all' | 'individual' | 'organization'>('all')
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; clientId: string | null; clientName: string }>({
    isOpen: false,
    clientId: null,
    clientName: ''
  })
  const [showAddModal, setShowAddModal] = useState(false)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return true; // Default to mobile if SSR
  })

  useEffect(() => {
    fetchClients()
  }, [])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fetchClients = async () => {
    try {
      // Fetch all parties (clients) with activity info
      const { data: partiesData, error: partiesError } = await supabase
        .from('parties_with_activity')
        .select(`
          *,
          advisor:platform_users!assigned_advisor_id (
            id,
            full_name,
            email
          ),
          client_mymeetings_mapping!portal_party_id (
            id,
            mymeetings_party_name
          ),
          project_parties (
            id
          )
        `)
        .order('created_at', { ascending: false })

      if (partiesError) throw partiesError

      // Transform data to include project count
      const partiesWithCounts = (partiesData || []).map(party => ({
        ...party,
        project_count: party.project_parties?.length || 0
      }))

      setParties(partiesWithCounts)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching parties:', error);
      }
    } finally {
      setLoading(false)
    }
  }

  const filteredParties = parties.filter(party => {
    const matchesSearch = party.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         party.primary_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         party.advisor?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = filterStatus === 'all' || party.status === filterStatus
    const matchesType = filterType === 'all' || party.type === filterType
    
    return matchesSearch && matchesStatus && matchesType
  })

  const handleDeleteClient = async (clientId: string) => {
    try {
      const { error } = await supabase
        .from('parties')
        .delete()
        .eq('id', clientId)

      if (error) throw error
      
      showSuccess('Client deleted successfully')
      fetchClients()
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error deleting client:', error);
        }

      }
      showError('Failed to delete client')
    }
  }

  const handleArchiveClient = async (clientId: string) => {
    try {
      const { error } = await supabase
        .from('parties')
        .update({ status: 'archived' })
        .eq('id', clientId)

      if (error) throw error
      
      showSuccess('Client archived successfully')
      fetchClients()
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error archiving client:', error);
        }

      }
      showError('Failed to archive client')
    }
  }

  if (!hasRole(['super_admin', 'pcm_staff'])) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">You don't have permission to view this page.</p>
      </div>
    )
  }

  return (
    <div className="portal-page-bg min-h-screen">
      <div className="py-4 sm:py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Actions Bar */}
        <div className="mb-4 sm:mb-6 flex justify-end">
          <button
            onClick={() => setShowAddModal(true)}
            className="portal-button-primary inline-flex items-center text-sm sm:text-base"
          >
            <PlusIcon className="-ml-0.5 mr-1.5 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden sm:inline">Add New Client</span>
            <span className="sm:hidden">Add Client</span>
          </button>
        </div>

        {/* Filters */}
        <div className="space-y-3 sm:space-y-0 sm:flex sm:gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="portal-input pl-10"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:flex sm:gap-4">
            <select
              className="portal-input"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
            <select
              className="portal-input"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
            >
              <option value="all">All Types</option>
              <option value="individual">Individuals</option>
              <option value="organization">Organizations</option>
            </select>
          </div>
        </div>

        {/* Client List */}
        <div className="mt-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-amber-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading clients...
              </div>
            </div>
          ) : filteredParties.length === 0 ? (
            <div className="portal-card">
              <div className="px-4 py-5 sm:p-6 text-center">
                <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No clients found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm ? 'Try adjusting your search terms.' : 'Get started by adding your first client.'}
                </p>
                {!searchTerm && (
                  <div className="mt-6">
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                    >
                      <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                      Add New Client
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {filteredParties.map((party) => (
                  <div key={party.id} className="portal-card">
                    <div className="p-4">
                      {/* Header Section */}
                      <div className="flex items-start justify-between mb-3">
                        <Link to={`/portal/clients/${party.id}`} className="flex items-start flex-1 min-w-0 group">
                          <div className="flex-shrink-0 mr-3">
                            {party.type === 'individual' ? (
                              <UserIcon className="h-10 w-10 text-gray-400 group-hover:text-gray-600" />
                            ) : (
                              <BuildingOfficeIcon className="h-10 w-10 text-gray-400 group-hover:text-gray-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-gray-900 truncate">{party.name}</h3>
                            <p className="text-sm text-gray-500">
                              {party.type === 'individual' ? 'Individual' : 'Organization'}
                              {party.tax_id && ` • ${party.tax_id}`}
                            </p>
                          </div>
                        </Link>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            party.status === 'active' ? 'bg-green-100 text-green-800' :
                            party.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {party.status}
                          </span>
                          <ThreeDotsMenu
                            items={[
                              {
                                label: 'Edit',
                                onClick: () => window.location.href = `/portal/clients/${party.id}`,
                                icon: <PencilIcon className="h-4 w-4" />
                              },
                              {
                                label: 'Archive',
                                onClick: () => handleArchiveClient(party.id),
                                icon: <ArchiveBoxIcon className="h-4 w-4" />,
                                className: party.status === 'archived' ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:text-gray-900'
                              },
                              {
                                divider: true
                              },
                              {
                                label: 'Delete',
                                onClick: () => setDeleteDialog({ isOpen: true, clientId: party.id, clientName: party.name }),
                                icon: <TrashIcon className="h-4 w-4" />,
                                className: 'text-red-600 hover:text-red-700'
                              }
                            ].filter(item => item.label !== 'Archive' || party.status !== 'archived')}
                          />
                        </div>
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-2 mb-3">
                        {party.primary_email && (
                          <div className="flex items-center text-sm text-gray-600">
                            <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                            <span className="truncate">{party.primary_email}</span>
                          </div>
                        )}
                        {party.advisor && (
                          <div className="flex items-center text-sm text-gray-600">
                            <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                            <span className="truncate">Advisor: {party.advisor.full_name}</span>
                          </div>
                        )}
                        <div className="flex items-center text-sm text-gray-600">
                          <BriefcaseIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                          <span>{party.project_count || 0} {party.project_count === 1 ? 'Project' : 'Projects'}</span>
                        </div>
                      </div>

                      {/* Footer Section */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="flex items-center text-xs text-gray-500">
                          <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                          {new Date(party.created_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          {party.days_since_contact !== null && (
                            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
                              party.days_since_contact === 0 ? 'bg-green-100 text-green-800' :
                              party.days_since_contact <= 7 ? 'bg-blue-100 text-blue-800' :
                              party.days_since_contact <= 30 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              <ClockIcon className="h-3 w-3 mr-1" />
                              {party.last_contact_label}
                            </div>
                          )}
                          {party.client_mymeetings_mapping && party.client_mymeetings_mapping.length > 0 && (
                            <div className="flex items-center text-xs text-blue-600" title="Linked to MyMeetings">
                              <LinkIcon className="h-3 w-3 mr-1" />
                              <span>MyMeetings</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <div className="portal-table-container">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Client
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Advisor
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Projects
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Activity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="relative px-6 py-3">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredParties.map((party) => (
                        <tr key={party.id} className="portal-table-row hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link to={`/portal/clients/${party.id}`} className="flex items-center group">
                              <div className="flex-shrink-0">
                                {party.type === 'individual' ? (
                                  <UserIcon className="h-10 w-10 text-gray-400 group-hover:text-gray-600" />
                                ) : (
                                  <BuildingOfficeIcon className="h-10 w-10 text-gray-400 group-hover:text-gray-600" />
                                )}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{party.name}</div>
                                <div className="text-sm text-gray-500">
                                  {party.type === 'individual' ? 'Individual' : 'Organization'}
                                  {party.tax_id && ` • ${party.tax_id}`}
                                </div>
                              </div>
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {party.primary_email && (
                              <div className="flex items-center text-sm text-gray-500">
                                <EnvelopeIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                                <span className="truncate max-w-xs">{party.primary_email}</span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {party.advisor && (
                              <div className="text-sm text-gray-900">{party.advisor.full_name}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <BriefcaseIcon className="h-4 w-4 text-gray-400 mr-1.5" />
                              <span>{party.project_count || 0}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {party.days_since_contact !== null && (
                                <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
                                  party.days_since_contact === 0 ? 'bg-green-100 text-green-800' :
                                  party.days_since_contact <= 7 ? 'bg-blue-100 text-blue-800' :
                                  party.days_since_contact <= 30 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  <ClockIcon className="h-3 w-3 mr-1" />
                                  {party.last_contact_label}
                                </div>
                              )}
                              {party.client_mymeetings_mapping && party.client_mymeetings_mapping.length > 0 && (
                                <div className="flex items-center text-sm text-blue-600" title="Linked to MyMeetings">
                                  <LinkIcon className="h-4 w-4 mr-1" />
                                  <span className="text-xs">MyMeetings</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              party.status === 'active' ? 'bg-green-100 text-green-800' :
                              party.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {party.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <ThreeDotsMenu
                              items={[
                                {
                                  label: 'Edit',
                                  onClick: () => window.location.href = `/portal/clients/${party.id}`,
                                  icon: <PencilIcon className="h-4 w-4" />
                                },
                                {
                                  label: 'Archive',
                                  onClick: () => handleArchiveClient(party.id),
                                  icon: <ArchiveBoxIcon className="h-4 w-4" />,
                                  className: party.status === 'archived' ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:text-gray-900'
                                },
                                {
                                  divider: true
                                },
                                {
                                  label: 'Delete',
                                  onClick: () => setDeleteDialog({ isOpen: true, clientId: party.id, clientName: party.name }),
                                  icon: <TrashIcon className="h-4 w-4" />,
                                  className: 'text-red-600 hover:text-red-700'
                                }
                              ].filter(item => item.label !== 'Archive' || party.status !== 'archived')}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteDialog.isOpen}
          onClose={() => setDeleteDialog({ isOpen: false, clientId: null, clientName: '' })}
          onConfirm={() => {
            if (deleteDialog.clientId) {
              handleDeleteClient(deleteDialog.clientId)
            }
          }}
          title="Delete Client"
          message={`Are you sure you want to delete ${deleteDialog.clientName}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          isDestructive={true}
        />

        {/* Add Client Modal */}
        <AddPartyModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchClients}
        />
      </div>
    </div>
    </div>
  )
}